import { useEffect, useRef, useState } from 'react';
import { COUNTRIES, COUNTRY_MAP } from '@/lib/countries';

interface CountryMultiSelectProps {
  value: string[];
  onChange: (codes: string[]) => void;
  placeholder?: string;
  emptyHint?: string;
  size?: 'sm' | 'md';
  /** When provided, the dropdown list is limited to these ISO codes. */
  restrictTo?: string[];
}

export function CountryMultiSelect({
  value,
  onChange,
  placeholder = 'Search countries…',
  emptyHint,
  size = 'md',
  restrictTo,
}: CountryMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const source = restrictTo?.length
    ? COUNTRIES.filter((c) => restrictTo.includes(c.code))
    : COUNTRIES;
  const filtered = source.filter((c) => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
  });

  function toggle(code: string) {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  }

  function remove(code: string) {
    onChange(value.filter((c) => c !== code));
  }

  const inputCls = size === 'sm'
    ? 'block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary'
    : 'block w-full px-3 py-2 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary';

  return (
    <div ref={wrapRef} className="relative">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((code) => {
            const c = COUNTRY_MAP[code];
            return (
              <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs">
                <span>{c?.flag || '🌍'}</span>
                <span>{c?.name || code}</span>
                <button type="button" onClick={() => remove(code)} className="ml-1 hover:text-primary/70">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={value.length > 0 ? 'Add more countries…' : placeholder}
          className={inputCls}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <svg className="w-4 h-4 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-surface border border-border rounded-md shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-content-subtle">No countries found</div>
          ) : (
            filtered.map((c) => {
              const isSelected = value.includes(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggle(c.code)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-surface-muted ${isSelected ? 'bg-primary/5' : ''}`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                  <span className="text-xs text-content-subtle">{c.code}</span>
                  {isSelected && (
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}

      {emptyHint && value.length === 0 && (
        <p className="mt-1 text-xs text-content-subtle">{emptyHint}</p>
      )}
    </div>
  );
}
